import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  romaniaLocations,
  romaniaZoneTabs,
  type RomaniaLocation,
  type RomaniaZoneTab,
} from '@/data/romaniaLocations';
import { MapPin, Play, Pause, BedDouble, ArrowRight, Compass } from 'lucide-react';

const scrollToPlan = () => {
  const el = document.getElementById('plan-journey');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

/* ── Location card: click-to-autoscroll photo carousel + hover hotels popup ── */
const LocationCard: React.FC<{ loc: RomaniaLocation; index: number }> = ({ loc, index }) => {
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Auto-scroll the pictures while playing (started by clicking the photo).
  useEffect(() => {
    if (!playing || loc.images.length < 2) return;
    const t = setInterval(() => {
      setCurrent((i) => (i + 1) % loc.images.length);
    }, 1800);
    return () => clearInterval(t);
  }, [playing, loc.images.length]);

  return (
    <div
      className="group flex flex-col animate-fade-in"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* ── Carousel ── */}
      <button
        type="button"
        onClick={() => setPlaying((p) => !p)}
        aria-label={playing ? `Pause ${loc.name} photo tour` : `Play ${loc.name} photo tour`}
        className="relative block w-full aspect-[4/3] overflow-hidden text-left cursor-pointer"
      >
        {loc.images.map((img, i) => (
          <img
            key={img.src + i}
            src={img.src}
            alt={img.alt}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
              i === current ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10" />

        {/* Zone badge */}
        <span className="absolute top-4 right-4 font-sans text-[9px] tracking-[0.25em] uppercase px-3 py-1.5 bg-[#1A3B5C]/85 text-white backdrop-blur-sm">
          {loc.zone}
        </span>

        {/* Play / pause hint */}
        <span className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm text-[#1A3B5C]">
          {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          <span className="font-sans text-[9px] tracking-[0.2em] uppercase">
            {playing ? 'Pause tour' : 'Click for photo tour'}
          </span>
        </span>

        {/* Dots */}
        {loc.images.length > 1 && (
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {loc.images.map((_, i) => (
              <span
                key={i}
                className={`transition-all duration-500 ${
                  i === current ? 'w-6 h-[2px] bg-[#C9A961]' : 'w-3 h-[2px] bg-white/50'
                }`}
              />
            ))}
          </span>
        )}
      </button>

      {/* ── Info block (hovering it reveals the hotels popup card) ── */}
      <div className="group/info relative pt-5">
        {/* Popup card with the location's hotels & destinations */}
        <div className="pointer-events-none group-hover/info:pointer-events-auto absolute bottom-full left-0 right-0 mb-3 z-30 opacity-0 translate-y-3 group-hover/info:opacity-100 group-hover/info:translate-y-0 transition-all duration-300">

          <div className="bg-white shadow-2xl border border-[#E8DFD0]">
            <div className="flex items-center gap-2 px-5 py-3 bg-[#1A3B5C]">
              <BedDouble className="w-3.5 h-3.5 text-[#C9A961]" />
              <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-white">
                Stays in {loc.name}
              </span>
            </div>
            <ul className="max-h-56 overflow-y-auto divide-y divide-[#F0EADC]">
              {loc.hotels.map((h) => (
                <li key={h.id}>
                  <Link
                    to="/romania/destinations"
                    className="flex items-start justify-between gap-3 px-5 py-3 hover:bg-[#FAF8F3] transition-colors"
                  >
                    <span>
                      <span className="block font-serif text-sm text-[#1A3B5C] leading-snug">
                        {h.name}
                      </span>
                      <span className="block font-sans text-[10px] tracking-wider text-[#999] mt-0.5">
                        {h.location}
                      </span>
                    </span>
                    <ArrowRight className="w-3 h-3 text-[#C9A961] mt-1 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8DFD0] bg-[#FAF8F3]">
              <Link
                to="/romania/destinations"
                className="font-sans text-[9px] tracking-[0.2em] uppercase text-[#1A3B5C] hover:text-[#C9A961] transition-colors flex items-center gap-1.5"
              >
                <Compass className="w-3 h-3" /> Explore all
              </Link>
              <Link
                to="/romania/booking"
                className="book-cta font-sans text-[9px] tracking-[0.2em] uppercase px-4 py-2 bg-[#1A3B5C] text-white hover:bg-[#C9A961] transition-colors"
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-3 h-3 text-[#C9A961]" />
          <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999]">
            {loc.tagline} · {loc.zone} Romania
          </span>
        </div>
        <h3 className="font-serif text-xl md:text-2xl text-[#1A3B5C] group-hover:text-[#C9A961] transition-colors mb-2">
          {loc.name}
        </h3>
        <p className="font-sans text-xs text-[#888] leading-relaxed mb-3 line-clamp-2">
          {loc.description}
        </p>
        <div className="flex items-center justify-between">
          <button
            onClick={scrollToPlan}
            className="book-cta font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
          >
            Book Now
          </button>
          <span className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#C9A961] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {loc.hotels.length} stays <BedDouble className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
};

/* ── Section: Romanian Destinations in the PDF order, N/C/S tabs ── */
const RomaniaLocationsGrid: React.FC = () => {
  const [activeZone, setActiveZone] = useState<RomaniaZoneTab>('All');

  const filtered =
    activeZone === 'All'
      ? romaniaLocations
      : romaniaLocations.filter((l) => l.zone === activeZone);

  return (
    <section id="destinations" className="py-24 lg:py-32 bg-[#FAF8F3]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
            Our Collection
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6 gold-underline">
            Romanian Destinations
          </h2>
          <p className="font-sans text-sm text-[#777] max-w-lg mx-auto leading-relaxed mt-10">
            From the painted north to the wild southern delta — hover a destination to see its
            hotels, and click any photo to tour it.
          </p>
        </div>

        {/* North / Central / South tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-14">
          {romaniaZoneTabs.map((zone) => (
            <button
              key={zone}
              onClick={() => setActiveZone(zone)}
              className={`font-sans text-[10px] tracking-[0.2em] uppercase px-6 py-2.5 transition-all duration-300 ${
                activeZone === zone
                  ? 'bg-[#1A3B5C] text-white'
                  : 'bg-transparent text-[#777] hover:text-[#1A3B5C] border border-[#ddd] hover:border-[#1A3B5C]'
              }`}
            >
              {zone === 'All' ? 'All Destinations' : zone}
            </button>
          ))}
        </div>

        {/* Grid — locations in the exact PDF order */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 lg:gap-x-8 gap-y-12">
          {filtered.map((loc, index) => (
            <LocationCard key={loc.id} loc={loc} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default RomaniaLocationsGrid;
